// 语言注册表 —— 前端支持的语言集中在此登记，新增语言只需加一行
//
// code:   i18n 内部语言码（= 后端多语言字段键，如 mlName[code]；也是 i18n.language 的值）
// label:  UI 显示文本（语言切换按钮等）
// locale: sessionStorage('lang') 及 Accept-Language 请求头用的值
//
// 说明：此文件保持"轻量"（不 import 翻译包/接口），以便 initI18N 等底层模块安全引用。
export const SUPPORTED_LANGUAGES = [
  { code: "zh", label: "中文", locale: "zh-CN,zh" },
  { code: "en", label: "EN", locale: "en-US,en" },
  { code: "ja", label: "日本語", locale: "ja-JP,ja" },
];

// 所有支持的语言码，如 ["zh","en","ja"]
export const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

// 历史别名归一化：老链接/二维码/小程序 webview 可能仍带 fallbackLng=cn（旧内部语言码），
// 统一归一为 zh，避免 'cn' 裸值流入 === 'zh' 判断或写出 mlName.cn 脏数据。
const CODE_ALIASES = { cn: "zh" };
export const normalizeCode = (code) => CODE_ALIASES[code] || code;

// 语言码 → locale（Accept-Language / sessionStorage.lang），未知回落首项（中文）
export const codeToLocale = (code) =>
  (SUPPORTED_LANGUAGES.find((l) => l.code === normalizeCode(code)) || SUPPORTED_LANGUAGES[0]).locale;

// locale → 语言码（反查），未知回落首项（中文）
export const localeToCode = (locale) =>
  (SUPPORTED_LANGUAGES.find((l) => l.locale === locale) || SUPPORTED_LANGUAGES[0]).code;

// 切换语言后同步地址栏上的 fallbackLng（不刷新页面）。
// - 只在 URL 已经显式带 fallbackLng 时改写，避免给没带该参数的页面凭空加参数；
// - 只做参数值级别的字符串替换，因此普通 query（xxx.html?a=1&fallbackLng=zh）与
//   hash 路由（international.html#/share_page?clientId=x&fallbackLng=zh）都适用，
//   也不会改动 hash 路由路径本身；
// - 用 history.replaceState 而非改 location.href：不产生历史条目、不触发导航、不重刷页面。
const FALLBACK_LNG_PATTERN = /([?&]fallbackLng=)[^&#]*/;

const getQuerySeparator = (value) => {
  if (value.includes("?")) {
    return value.endsWith("?") || value.endsWith("&") ? "" : "&";
  }
  return "?";
};

export const appendFallbackLngToUrl = (url, code) => {
  const lang = normalizeCode(code);
  if (!SUPPORTED_CODES.includes(lang) || !url) return url;

  const value = encodeURIComponent(lang);
  if (FALLBACK_LNG_PATTERN.test(url)) {
    return url.replace(FALLBACK_LNG_PATTERN, `$1${value}`);
  }

  const hashIndex = url.indexOf("#");
  if (hashIndex >= 0) {
    const beforeHash = url.slice(0, hashIndex);
    const hash = url.slice(hashIndex);
    if (hash.startsWith("#/") || hash.includes("?")) {
      return `${beforeHash}${hash}${getQuerySeparator(hash)}fallbackLng=${value}`;
    }
    return `${beforeHash}${getQuerySeparator(beforeHash)}fallbackLng=${value}${hash}`;
  }

  return `${url}${getQuerySeparator(url)}fallbackLng=${value}`;
};

export const syncFallbackLngInUrl = (code, { addIfMissing = false } = {}) => {
  const lang = normalizeCode(code);
  if (!SUPPORTED_CODES.includes(lang)) return;
  try {
    const href = window.location.href;
    const next = FALLBACK_LNG_PATTERN.test(href)
      ? appendFallbackLngToUrl(href, lang)
      : addIfMissing
        ? appendFallbackLngToUrl(href, lang)
        : href;
    if (next === href) return;
    window.history.replaceState(null, "", next);
  } catch (e) {
    // 异常环境（如 file://、沙箱 iframe）下 replaceState 可能抛错，静默忽略即可
  }
};

// 浏览器语言（navigator.language，如 "ja-JP" / "zh-cn" / "en-US"）→ locale
// 按主语言子标签（'-' 前一段）匹配注册表，命中即返回对应 locale（如日语 → 'ja-JP,ja'）；
// 未命中回落 fallback（默认英文，兼容海外首访旧行为）。
// 这样浏览器语言检测不再是"非中文即英文"的二元判断，而是走 langConfig 全局映射。
export const browserLangToLocale = (browserLang, fallback = "en") => {
  const primary = String(browserLang || "").toLowerCase().split("-")[0];
  const matched = SUPPORTED_LANGUAGES.find((l) => l.code === primary);
  const fallbackLang =
    SUPPORTED_LANGUAGES.find((l) => l.code === fallback) || SUPPORTED_LANGUAGES[0];
  return (matched || fallbackLang).locale;
};
